import { useCallback, useEffect } from 'react';

import { useRouter } from 'next/navigation';
import useRazorpay, { RazorpayOptions } from 'react-razorpay';

import { useAppDispatch, useAppSelector } from '@hooks/store';
import useAuthSelector from '@hooks/useAuthSelector';
import { paths } from '@routes/path';
import { showToast } from '@slices/apiSlice';

import { useBuyPlanMutation } from '../api';

interface RazorPayProps {
    id: string;
}
export default function useRazorPay({ id }: RazorPayProps) {
    const [buyPlan, { isLoading, isError, error }] = useBuyPlanMutation();
    const { user } = useAuthSelector();
    const dispatch = useAppDispatch();
    const router = useRouter();
    const [Razorpay] = useRazorpay();

    const redirectPath = useAppSelector(state => state.reducer.booking.redirectPath);
   
    useEffect(() => {
        if (isError) {
            const errorDescription = error && Array.isArray(error) ? error[0] : error;
            dispatch(showToast({ variant: 'error', description: errorDescription }));
        }
    }, [isError, error, dispatch]);

    const handlePayment = useCallback(async () => {
        const data = await buyPlan({
            planId: `${id}`,
        }).unwrap();
        const options: RazorpayOptions = {
            key: process.env.NEXT_PUBLIC_RAZORPAY_KEY!,
            amount: `${data?.price}`,
            currency: data?.currency,
            name: 'YourYoda',
            description: 'Transaction',
            order_id: data?.gatewayOrderId,
            handler: res => {
                if (res.razorpay_payment_id) {
                    router.push(redirectPath || paths.mentee.dashboard.billings);
                }
            },
            prefill: {
                name: user?.name,
                email: user?.email,
                contact: user?.phone,
            },
            notes: {
                address: 'Razorpay Corporate Office',
            },
            theme: {
                color: '#3399cc',
            },
        };

        const rzpay = new Razorpay(options);
        rzpay.on('payment.failed', (response: any) => {
            dispatch(
                showToast({
                    variant: 'error',
                    description: response.error.description ?? response.error.reason,
                })
            );
        });
        rzpay.open();
    }, [Razorpay, buyPlan, dispatch, id, router, user, redirectPath]);
    return {
        handlePayment,
        isLoading,
    };
}
